import { useMemo } from 'react';
import {
  approvedServiceLabel,
  serviceLockKey,
  type ApprovedBudget,
  type ServiceApproval,
} from '@obraplan/core/domain/takeoff/project-budget';
import type {
  CatalogDiscipline,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { readsSteelRate } from '@obraplan/core/domain/takeoff/steel';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';
import { dataEHora } from './exportar-orcamento';

/**
 * Os serviços aprovados no orçamento da obra, como a aba de itens os trava (Fase 33, RN-438).
 *
 * O servidor recusa a mudança no serviço aprovado; a tela não a oferece. São as mesmas fotos que
 * a aba Orçamento lê, pela chave da trava (`serviceLockKey`): na versão, o serviço "ESTACA (V2)"
 * é o "ESTACA" que a aprovada travou.
 */

/** A aprovação de um serviço, com o nome que ele tinha na foto. */
export interface AprovacaoDoServico extends ServiceApproval {
  readonly disciplineName: string;
  readonly serviceName: string;
}

/** Os serviços aprovados, pela chave da trava. */
export type ServicosAprovados = ReadonlyMap<string, AprovacaoDoServico>;

export function servicosAprovadosDe(approvals: readonly ApprovedBudget[]): ServicosAprovados {
  const aprovados = new Map<string, AprovacaoDoServico>();
  for (const approval of approvals) {
    for (const service of approval.services) {
      aprovados.set(service.key, {
        disciplineName: service.disciplineName,
        serviceName: service.serviceName,
        approvedAt: approval.approvedAt,
        ...(approval.approvedByName === undefined
          ? {}
          : { approvedByName: approval.approvedByName }),
      });
    }
  }
  return aprovados;
}

/** A aprovação do serviço desta chave do catálogo — `undefined` quando ele está aberto. */
export function aprovacaoDe(
  aprovados: ServicosAprovados | undefined,
  serviceKey: string,
): AprovacaoDoServico | undefined {
  return aprovados?.get(serviceLockKey(serviceKey));
}

/**
 * Os serviços aprovados da disciplina que leem a taxa de aço dela (`readsSteelRate`): com um
 * deles, a taxa não muda mais. Vazio = a taxa é livre.
 */
export function acoAprovadoDa(
  aprovados: ServicosAprovados | undefined,
  disciplina: CatalogDiscipline,
): readonly AprovacaoDoServico[] {
  if (aprovados === undefined || aprovados.size === 0) return [];
  return disciplina.services.flatMap((servico) => {
    const aprovacao = aprovacaoDe(aprovados, servico.key);
    return aprovacao !== undefined && readsSteelRate(servico) ? [aprovacao] : [];
  });
}

/**
 * O catálogo sem os serviços aprovados — onde o código informado procura a quem propagar
 * (`itensParaPropagarCodigo`, RN-396). O item do aprovado não recebe código (RN-438): o código
 * muda a identidade do material, e o servidor recusaria.
 */
export function semOsAprovados(
  catalog: ServiceCatalog,
  aprovados: ServicosAprovados | undefined,
): ServiceCatalog {
  if (aprovados === undefined || aprovados.size === 0) return catalog;
  return {
    ...catalog,
    disciplines: catalog.disciplines.map((disciplina) => ({
      ...disciplina,
      services: disciplina.services.filter(
        (servico) => aprovacaoDe(aprovados, servico.key) === undefined,
      ),
    })),
  };
}

/** "aprovado em 19/10/2026 14:30 por Ana" — ao lado do cadeado do serviço. */
export function textoDaAprovacao(aprovacao: ServiceApproval): string {
  return `aprovado em ${dataEHora(aprovacao.approvedAt)}${aprovacao.approvedByName === undefined ? '' : ` por ${aprovacao.approvedByName}`}`;
}

/** "ESTRUTURA › CORTE E DOBRA DE FERRAGEM" — como o servidor e a recusa o nomeiam. */
export function nomeDoAprovado(aprovacao: AprovacaoDoServico): string {
  return approvedServiceLabel(aprovacao);
}

/**
 * A leitura viva das aprovações da obra aberta. `undefined` enquanto ela não chega — a tela
 * fica como estava, e o servidor segura o que passar.
 */
export function useServicosAprovados(): ServicosAprovados | undefined {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  // `async` de propósito: a falha vira rejeição, que a leitura viva já trata, e não uma exceção
  // solta no efeito.
  const lidas = useLive(async () => {
    if (session === undefined || activeProjectId === undefined) return undefined;
    return container.getProjectApprovedBudget.execute({ projectId: activeProjectId }, session);
  }, [session?.userId, activeProjectId]);
  const approvals = lidas !== undefined && lidas.ok ? lidas.value.approvals : undefined;
  return useMemo(
    () => (approvals === undefined ? undefined : servicosAprovadosDe(approvals)),
    [approvals],
  );
}
